import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { userMayUseActivitiesImportPage } from "../lib/adminImport";
import { runActivitiesImport } from "../api/activitiesImport";
import {
  fetchActivitySources,
  fetchPendingLocationReviews,
  patchActivitySourceSingleEventMode,
  runLocationReviewAction,
} from "../api/activitySources";
import {
  fetchImportBatches,
  fetchImportBatchDetail,
  retryImportBatchWrite,
  startImportAllSources,
} from "../api/importBatches";
import SourceSubmissionReviewPanel from "../components/SourceSubmissionReview/SourceSubmissionReviewPanel";
import "./ActivitiesImportPage.css";

const MAX_HISTORY = 8;
const DEFAULT_MAX_DETAIL_URLS = 20;

function formatWhen(startsAt, endsAt) {
  if (!startsAt) return "";
  try {
    const start = new Date(startsAt).toLocaleString("en-GB", {
      dateStyle: "short",
      timeStyle: "short",
    });
    if (!endsAt) return start;
    const end = new Date(endsAt).toLocaleString("en-GB", {
      dateStyle: "short",
      timeStyle: "short",
    });
    return `${start} - ${end}`;
  } catch {
    return String(startsAt);
  }
}

export default function ActivitiesImportPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [dryRunOnly, setDryRunOnly] = useState(true);
  const [maxDetailUrls, setMaxDetailUrls] = useState(DEFAULT_MAX_DETAIL_URLS);
  const [archiveGraceHours, setArchiveGraceHours] = useState(72);
  const [useCrossSource, setUseCrossSource] = useState(true);
  const [allowLlmFallback, setAllowLlmFallback] = useState(true);
  const [singleEventDetailPage, setSingleEventDetailPage] = useState(false);
  const [sourceUrl, setSourceUrl] = useState("");
  const [sources, setSources] = useState([]);
  const [sourcesStatus, setSourcesStatus] = useState("idle");
  const [sourcesError, setSourcesError] = useState("");
  const [selectedSourceId, setSelectedSourceId] = useState("");
  const [useCustomUrl, setUseCustomUrl] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [pendingLocations, setPendingLocations] = useState([]);
  const [pendingStatus, setPendingStatus] = useState("idle");
  const [pendingError, setPendingError] = useState("");
  const [reviewActionBusyId, setReviewActionBusyId] = useState("");

  const [batchList, setBatchList] = useState([]);
  const [batchListLoading, setBatchListLoading] = useState(false);
  const [batchListError, setBatchListError] = useState("");
  const [batchAllBusy, setBatchAllBusy] = useState(false);
  const [activeBatchId, setActiveBatchId] = useState("");
  const [batchDetail, setBatchDetail] = useState(null);
  const [batchDetailError, setBatchDetailError] = useState("");
  const [batchWriteOnlyIfDryOk, setBatchWriteOnlyIfDryOk] = useState(true);
  const [batchRetryBusy, setBatchRetryBusy] = useState(false);

  const prevSelectedSourceIdRef = useRef("");
  const prevUseCustomUrlRef = useRef(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate(`/login?redirect=${encodeURIComponent("/admin/activities-import")}`, {
        replace: true,
      });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (loading || !user || !userMayUseActivitiesImportPage(user)) return;

    let cancelled = false;
    (async () => {
      setSourcesStatus("loading");
      setSourcesError("");
      try {
        const list = await fetchActivitySources();
        if (cancelled) return;
        setSources(list);
        setSourcesStatus("ok");
        setSelectedSourceId((prev) => {
          if (list.length === 0) return "";
          if (prev && list.some((s) => s.id === prev)) return prev;
          return list[0].id;
        });
      } catch (e) {
        if (cancelled) return;
        setSourcesStatus("error");
        setSourcesError(e?.message || String(e));
        setUseCustomUrl(true);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depend on user.id only so auth object churn does not refetch and reset the form
  }, [loading, user?.id]);

  useEffect(() => {
    if (loading || !user || !userMayUseActivitiesImportPage(user)) return;
    let cancelled = false;
    (async () => {
      setPendingStatus("loading");
      setPendingError("");
      try {
        const list = await fetchPendingLocationReviews({ limit: 120 });
        if (cancelled) return;
        setPendingLocations(list);
        setPendingStatus("ok");
      } catch (e) {
        if (cancelled) return;
        setPendingStatus("error");
        setPendingError(e?.message || String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depend on user.id only so auth object churn does not refetch and reset review queue
  }, [loading, user?.id]);

  useEffect(() => {
    if (loading || !user || !userMayUseActivitiesImportPage(user)) return;
    let cancelled = false;
    (async () => {
      setBatchListLoading(true);
      setBatchListError("");
      try {
        const list = await fetchImportBatches();
        if (cancelled) return;
        setBatchList(Array.isArray(list) ? list : []);
      } catch (e) {
        if (cancelled) return;
        setBatchListError(e?.message || String(e));
      } finally {
        if (!cancelled) setBatchListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depend on user.id only
  }, [loading, user?.id]);

  useEffect(() => {
    if (!activeBatchId) return undefined;
    let cancelled = false;
    let timerId = null;
    const tick = async () => {
      try {
        const detail = await fetchImportBatchDetail(activeBatchId);
        if (cancelled) return;
        setBatchDetail(detail);
        setBatchDetailError("");
        const st = detail.batch?.status;
        if (st === "completed" || st === "failed") {
          if (timerId != null) clearInterval(timerId);
          try {
            const list = await fetchImportBatches();
            if (!cancelled) setBatchList(Array.isArray(list) ? list : []);
          } catch {
            /* ignore */
          }
        }
      } catch (e) {
        if (!cancelled) {
          setBatchDetailError(e?.message || String(e));
        }
      }
    };
    tick();
    timerId = setInterval(tick, 3000);
    return () => {
      cancelled = true;
      if (timerId != null) clearInterval(timerId);
    };
  }, [activeBatchId]);

  useEffect(() => {
    if (useCustomUrl) {
      prevUseCustomUrlRef.current = true;
      return;
    }
    if (sourcesStatus !== "ok" || sources.length === 0) return;
    const src = sources.find((s) => s.id === selectedSourceId);
    if (!src) return;

    const selectionChanged = prevSelectedSourceIdRef.current !== selectedSourceId;
    const customTurnedOff =
      prevUseCustomUrlRef.current === true && useCustomUrl === false;

    prevSelectedSourceIdRef.current = selectedSourceId;
    prevUseCustomUrlRef.current = useCustomUrl;

    if (!selectionChanged && !customTurnedOff) {
      return;
    }

    setSourceUrl(src.sourceUrl);
    setMaxDetailUrls(String(src.defaultMaxDetailUrls));
    setAllowLlmFallback(src.defaultAllowLlmFallback);
    setSingleEventDetailPage(!!src.defaultSingleEventDetailPage);
  }, [selectedSourceId, sources, sourcesStatus, useCustomUrl]);

  if (loading || !user) {
    return (
      <div className="activities-import-page" aria-busy="true">
        <p>Loading…</p>
      </div>
    );
  }

  if (!userMayUseActivitiesImportPage(user)) {
    return (
      <div className="activities-import-page">
        <h1>Activities import</h1>
        <p className="activities-import-intro">
          This page is only available to admin accounts. Mark your user as admin
          in Supabase (e.g.{" "}
          <code style={{ fontSize: "0.85em" }}>app_metadata.role = "admin"</code>{" "}
          or{" "}
          <code style={{ fontSize: "0.85em" }}>app_metadata.is_admin = true</code>
          ).
        </p>
        <Link to="/">Home</Link>
      </div>
    );
  }

  const handleRun = async () => {
    setError("");
    const trimmedUrl = sourceUrl.trim();
    if (!trimmedUrl) {
      setError("Enter a start URL to run the import.");
      return;
    }
    setRunning(true);
    setLastResult(null);
    const started = new Date().toISOString();
    try {
      const data = await runActivitiesImport({
        dryRun: dryRunOnly,
        maxDetailUrls: Number(maxDetailUrls) || DEFAULT_MAX_DETAIL_URLS,
        archiveGraceHours: Number(archiveGraceHours) || 72,
        crossSourceDedupe: useCrossSource,
        allowLlmFallback,
        singleEventDetailPage:
          singleEventDetailPage && allowLlmFallback,
        singleEventDetailPagePreference: singleEventDetailPage,
        sourceUrl: trimmedUrl,
        customSourceUrl: useCustomUrl,
      });
      setLastResult(data);
      setHistory((prev) => {
        const entry = {
          at: started,
          dryRun: data.dryRun,
          ok: true,
          counters: data.counters,
        };
        return [entry, ...prev].slice(0, MAX_HISTORY);
      });
    } catch (e) {
      const msg = e?.message || String(e);
      setError(msg);
      setHistory((prev) => {
        const entry = { at: started, dryRun: dryRunOnly, ok: false, error: msg };
        return [entry, ...prev].slice(0, MAX_HISTORY);
      });
    } finally {
      setRunning(false);
    }
  };

  const logText = lastResult
    ? JSON.stringify(lastResult, null, 2)
    : "";

  const copyLog = async () => {
    if (!logText) return;
    try {
      await navigator.clipboard.writeText(logText);
    } catch {
      /* ignore */
    }
  };

  const refreshPendingLocations = async () => {
    setPendingStatus("loading");
    setPendingError("");
    try {
      const sourceScope =
        !useCustomUrl && sourcesStatus === "ok" && selectedSourceId
          ? sources.find((s) => s.id === selectedSourceId)?.slug
          : "";
      const list = await fetchPendingLocationReviews({
        limit: 120,
        sourceScope: sourceScope || undefined,
      });
      setPendingLocations(list);
      setPendingStatus("ok");
    } catch (e) {
      setPendingStatus("error");
      setPendingError(e?.message || String(e));
    }
  };

  const handleReviewAction = async (item, action) => {
    if (!item?.id || !action) return;
    setPendingError("");
    setReviewActionBusyId(item.id);
    try {
      let mergedIntoLocationId = "";
      if (action === "merge") {
        // Minimal UI for now: admin can paste target active location UUID.
        mergedIntoLocationId = window.prompt(
          "Enter target active location ID to merge into:",
          ""
        );
        if (!mergedIntoLocationId) {
          setReviewActionBusyId("");
          return;
        }
      }
      await runLocationReviewAction({
        action,
        locationId: item.id,
        mergedIntoLocationId,
      });
      await refreshPendingLocations();
    } catch (e) {
      setPendingError(e?.message || String(e));
    } finally {
      setReviewActionBusyId("");
    }
  };

  const selectedSourceNotes =
    sourcesStatus === "ok" && selectedSourceId
      ? sources.find((s) => s.id === selectedSourceId)?.notes
      : null;

  const batchCanRetryFailedWrites =
    Array.isArray(batchDetail?.sources) &&
    batchDetail.sources.some(
      (s) =>
        s.dryStatus === "ok" &&
        s.writeStatus === "failed" &&
        s.hasDryRowsSnapshot === true
    );

  const handleRetryBatchWrite = async () => {
    if (!activeBatchId || !batchCanRetryFailedWrites) return;
    setBatchDetailError("");
    setError("");
    setBatchRetryBusy(true);
    try {
      await retryImportBatchWrite(activeBatchId);
      const detail = await fetchImportBatchDetail(activeBatchId);
      setBatchDetail(detail);
      const list = await fetchImportBatches();
      setBatchList(Array.isArray(list) ? list : []);
    } catch (e) {
      const msg = e?.message || String(e);
      setBatchDetailError(msg);
      setError(msg);
    } finally {
      setBatchRetryBusy(false);
    }
  };

  const handleStartBatchAll = async () => {
    setBatchDetailError("");
    setError("");
    setBatchAllBusy(true);
    try {
      const id = await startImportAllSources({
        archiveGraceHours: Number(archiveGraceHours) || 72,
        crossSourceDedupe: useCrossSource,
        allowLlmFallback,
        writeOnlyIfDryOk: batchWriteOnlyIfDryOk,
      });
      setActiveBatchId(id);
      const detail = await fetchImportBatchDetail(id);
      setBatchDetail(detail);
      const list = await fetchImportBatches();
      setBatchList(Array.isArray(list) ? list : []);
    } catch (e) {
      const msg = e?.message || String(e);
      setBatchDetailError(msg);
      setError(msg);
    } finally {
      setBatchAllBusy(false);
    }
  };

  return (
    <div className="activities-import-page">
      <h1>Activities — full import</h1>
      <p className="activities-import-intro">
        Imports activities from a source stored in the database, or — if you
        enable a custom URL — from any start URL. For gz-zh.ch, GZ-specific
        logic is used (e.g. classification and normalization). A run may take
        several minutes.
      </p>

      <SourceSubmissionReviewPanel userMayUse={true} />

      <section className="activities-import-batch" aria-labelledby="activities-batch-heading">
        <h2 id="activities-batch-heading">Import all sources</h2>
        <p className="activities-import-intro" style={{ marginTop: 0 }}>
          Runs every <strong>enabled</strong> source in order: dry run per source (extract +
          normalize), then writes only those rows into the database — no second fetch or full
          re-import. If the dry snapshot cannot be stored or dry failed, that source’s write is
          skipped. Failures do not stop later sources. The job continues on the server after you
          leave this page. Requires <code>INGEST_CRON_SECRET</code> on the API host; writes need{" "}
          <code>INGEST_CRON_WRITE=1</code> and service role (same as cron writes).
        </p>
        <div className="activities-import-check">
          <label>
            <input
              type="checkbox"
              checked={batchWriteOnlyIfDryOk}
              onChange={(e) => setBatchWriteOnlyIfDryOk(e.target.checked)}
            />
            <span>Skip write for sources whose dry run failed</span>
          </label>
        </div>
        <div className="activities-import-actions" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="activities-import-run-btn"
            onClick={handleStartBatchAll}
            disabled={
              batchAllBusy ||
              sourcesStatus !== "ok" ||
              sources.length === 0 ||
              batchListLoading
            }
          >
            {batchAllBusy ? "Starting…" : "Run all sources (dry → write)"}
          </button>
        </div>
        {batchListError ? (
          <div className="activities-import-error" role="alert">
            Could not load batch history ({batchListError}).
          </div>
        ) : null}
        {batchDetailError ? (
          <div className="activities-import-error" role="alert">
            {batchDetailError}
          </div>
        ) : null}

        <h3 style={{ marginTop: 20, marginBottom: 8 }}>Recent batches</h3>
        {batchListLoading ? (
          <p className="activities-import-intro">Loading…</p>
        ) : batchList.length === 0 ? (
          <p className="activities-import-intro">No batches yet.</p>
        ) : (
          <ul className="activities-import-batch-list">
            {batchList.map((b) => (
              <li key={b.id}>
                <button
                  type="button"
                  className="activities-import-batch-link"
                  onClick={async () => {
                    setActiveBatchId(b.id);
                    setBatchDetailError("");
                    try {
                      const detail = await fetchImportBatchDetail(b.id);
                      setBatchDetail(detail);
                    } catch (e) {
                      setBatchDetailError(e?.message || String(e));
                    }
                  }}
                >
                  {b.id.slice(0, 8)}… — {b.status}
                  {b.createdAt
                    ? ` — ${new Date(b.createdAt).toLocaleString("en-GB")}`
                    : ""}
                </button>
              </li>
            ))}
          </ul>
        )}

        {activeBatchId && batchDetail?.batch ? (
          <div className="activities-import-batch-detail">
            <h3 style={{ marginTop: 16, marginBottom: 8 }}>
              Batch {batchDetail.batch.id.slice(0, 8)}… —{" "}
              <strong>{batchDetail.batch.status}</strong>
            </h3>
            {batchCanRetryFailedWrites ? (
              <div className="activities-import-actions" style={{ marginBottom: 12 }}>
                <button
                  type="button"
                  className="activities-import-run-btn"
                  onClick={handleRetryBatchWrite}
                  disabled={
                    batchRetryBusy ||
                    batchListLoading ||
                    ["queued", "running_dry", "running_write"].includes(
                      batchDetail.batch.status
                    )
                  }
                >
                  {batchRetryBusy
                    ? "Retrying write…"
                    : "Retry failed writes only (from dry snapshot)"}
                </button>
              </div>
            ) : null}
            {batchDetail.batch.errorSummary ? (
              <p className="activities-import-error" role="alert">
                {batchDetail.batch.errorSummary}
              </p>
            ) : null}
            <div className="activities-import-review-list">
              {(batchDetail.sources || []).map((row) => (
                <article
                  key={row.id}
                  className="activities-import-review-item"
                  style={{ marginBottom: 12 }}
                >
                  <h4 style={{ margin: "0 0 6px" }}>{row.name}</h4>
                  <p style={{ margin: "4px 0", fontSize: "0.9rem" }}>
                    Dry: <strong>{row.dryStatus}</strong>
                    {row.dryError ? ` — ${row.dryError}` : ""}
                    {" · "}Write: <strong>{row.writeStatus}</strong>
                    {row.writeError ? ` — ${row.writeError}` : ""}
                  </p>
                  {row.dryResult?.counters ? (
                    <p style={{ margin: "4px 0", fontSize: "0.85rem", opacity: 0.9 }}>
                      Dry counts: parsed {row.dryResult.counters.parsed ?? "—"}, inserted{" "}
                      {row.dryResult.counters.inserted ?? "—"}
                    </p>
                  ) : null}
                  {row.writeResult?.counters ? (
                    <p style={{ margin: "4px 0", fontSize: "0.85rem", opacity: 0.9 }}>
                      Write counts: parsed {row.writeResult.counters.parsed ?? "—"}, inserted{" "}
                      {row.writeResult.counters.inserted ?? "—"}
                    </p>
                  ) : null}
                </article>
              ))}
            </div>
          </div>
        ) : activeBatchId && !batchDetail?.batch ? (
          <p className="activities-import-intro">Loading batch…</p>
        ) : null}
      </section>

      {sourcesError ? (
        <div className="activities-import-error" role="alert">
          Could not load sources ({sourcesError}). You can enter a custom start
          URL below.
        </div>
      ) : null}

      <div className="activities-import-form">
        <div className="activities-import-check">
          <label>
            <input
              type="checkbox"
              checked={dryRunOnly}
              onChange={(e) => setDryRunOnly(e.target.checked)}
            />
            <span>
              Dry run only — counts items, does not write to the database.
            </span>
          </label>
        </div>

        {!dryRunOnly && (
          <p className="activities-import-warning">
            Warning: Without dry run, cards are inserted/updated and archiving
            may archive affected items after the grace period.
          </p>
        )}

        <div className="activities-import-field">
          <label htmlFor="activities-import-source">Import source</label>
          <select
            id="activities-import-source"
            className="activities-import-source-select"
            value={selectedSourceId}
            onChange={(e) => setSelectedSourceId(e.target.value)}
            disabled={
              sourcesStatus !== "ok" || sources.length === 0 || useCustomUrl
            }
          >
            {sourcesStatus === "loading" ? (
              <option value="">Loading…</option>
            ) : sources.length === 0 ? (
              <option value="">No sources</option>
            ) : (
              sources.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))
            )}
          </select>
          {selectedSourceNotes ? (
            <p className="activities-import-source-note">{selectedSourceNotes}</p>
          ) : null}
        </div>

        <div className="activities-import-check">
          <label>
            <input
              type="checkbox"
              checked={useCustomUrl}
              onChange={(e) => setUseCustomUrl(e.target.checked)}
            />
            <span>Custom start URL (debug / other domain)</span>
          </label>
        </div>

        <div className="activities-import-field">
          <label htmlFor="activities-source-url">
            Start URL
            {useCustomUrl || sourcesStatus !== "ok" || sources.length === 0
              ? " (manual)"
              : " (from source)"}
          </label>
          <input
            id="activities-source-url"
            type="url"
            placeholder="https://gz-zh.ch/"
            readOnly={
              !useCustomUrl && sourcesStatus === "ok" && sources.length > 0
            }
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              fontSize: "1rem",
              backgroundColor:
                !useCustomUrl && sourcesStatus === "ok" && sources.length > 0
                  ? "#f8fafc"
                  : "white",
            }}
          />
          {!useCustomUrl &&
          sourcesStatus === "ok" &&
          sources.length > 0 ? (
            <p className="activities-import-intro" style={{ marginTop: 6 }}>
              Without “custom URL”, the selected source’s URL is used. You do not
              need to leave this empty.
            </p>
          ) : useCustomUrl ? (
            <p className="activities-import-intro" style={{ marginTop: 6 }}>
              Enter a start URL — an import will not run while this field is empty.
            </p>
          ) : (
            <p className="activities-import-intro" style={{ marginTop: 6 }}>
              Enter a start URL or enable “Custom start URL” after sources load.
              An import will not run while this field is empty.
            </p>
          )}
        </div>

        <div className="activities-import-field">
          <label htmlFor="activities-max-detail">Max. offer detail URLs (sitemap)</label>
          <input
            id="activities-max-detail"
            type="number"
            min={0}
            max={200}
            value={maxDetailUrls}
            onChange={(e) => setMaxDetailUrls(e.target.value)}
          />
        </div>

        <div className="activities-import-field">
          <label htmlFor="activities-grace">Archive grace period (hours)</label>
          <input
            id="activities-grace"
            type="number"
            min={1}
            max={720}
            value={archiveGraceHours}
            onChange={(e) => setArchiveGraceHours(e.target.value)}
          />
        </div>

        <div className="activities-import-check">
          <label>
            <input
              type="checkbox"
              checked={useCrossSource}
              onChange={(e) => setUseCrossSource(e.target.checked)}
            />
            <span>Cross-source deduplication (title / location / time)</span>
          </label>
        </div>

        <div className="activities-import-check">
          <label>
            <input
              type="checkbox"
              checked={allowLlmFallback}
              onChange={(e) => setAllowLlmFallback(e.target.checked)}
            />
            <span>Use LLM fallback for pages without usable DOM / JSON-LD</span>
          </label>
        </div>

        <div className="activities-import-check">
          <label>
            <input
              type="checkbox"
              checked={singleEventDetailPage && allowLlmFallback}
              onChange={async (e) => {
                const checked = e.target.checked;
                setSingleEventDetailPage(checked);
                if (
                  useCustomUrl ||
                  sourcesStatus !== "ok" ||
                  !selectedSourceId
                ) {
                  return;
                }
                try {
                  const updated = await patchActivitySourceSingleEventMode({
                    id: selectedSourceId,
                    defaultSingleEventDetailPage: checked,
                  });
                  setSources((prev) =>
                    prev.map((s) =>
                      s.id === updated.id ? { ...s, ...updated } : s
                    )
                  );
                } catch (err) {
                  setError(err?.message || String(err));
                }
              }}
              disabled={!allowLlmFallback}
            />
            <span>
              Single event page (listing pass) — skip harvesting many event links;
              extract one focused activity via LLM for the start URL. JSON-LD on the
              page is still merged when present.
              {!useCustomUrl && sourcesStatus === "ok" ? (
                <> Saved per source when changed.</>
              ) : null}
            </span>
          </label>
          {!allowLlmFallback ? (
            <p className="activities-import-intro" style={{ margin: "6px 0 0", paddingLeft: 28 }}>
              Enable “LLM fallback” above to use this option.
            </p>
          ) : null}
        </div>

        <div className="activities-import-actions">
          <button
            type="button"
            className="activities-import-run-btn"
            onClick={handleRun}
            disabled={
              running ||
              sourcesStatus === "loading" ||
              !String(sourceUrl || "").trim()
            }
          >
            {running ? "Running…" : dryRunOnly ? "Start dry run" : "Start import"}
          </button>
          <Link to="/" className="activities-import-run-btn activities-import-run-btn-secondary" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            Back
          </Link>
        </div>
      </div>

      {error ? <div className="activities-import-error">{error}</div> : null}

      {lastResult ? (
        <div className="activities-import-log-wrap">
          {Array.isArray(lastResult?.debug?.sampleCards) &&
          lastResult.debug.sampleCards.length > 0 ? (
            <>
              <h2>Sample (extraction quality)</h2>
              <div className="activities-import-samples">
                {lastResult.debug.sampleCards.map((card, idx) => {
                  const whenText = formatWhen(card.starts_at, card.ends_at);
                  return (
                    <article
                      key={`${card.link || card.title || "sample"}-${idx}`}
                      className="activities-import-sample-card"
                    >
                      <h3>{card.title || "No title"}</h3>
                      {whenText ? (
                        <p className="activities-import-sample-meta">{whenText}</p>
                      ) : null}
                      {card.location ? (
                        <p className="activities-import-sample-meta">{card.location}</p>
                      ) : null}
                      {card.summary ? (
                        <p>{String(card.summary).slice(0, 220)}</p>
                      ) : null}
                      {card.link ? (
                        <a href={card.link} target="_blank" rel="noreferrer">
                          Open link
                        </a>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </>
          ) : null}

          <h2>Last response (debug)</h2>
          <div className="activities-import-log-actions">
            <button type="button" onClick={copyLog}>
              Copy JSON
            </button>
          </div>
          <pre className="activities-import-pre">{logText}</pre>
        </div>
      ) : null}

      <section className="activities-import-review">
        <div className="activities-import-review-head">
          <h2>Location review queue</h2>
          <button
            type="button"
            onClick={refreshPendingLocations}
            disabled={pendingStatus === "loading"}
          >
            {pendingStatus === "loading" ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        <p className="activities-import-intro" style={{ marginBottom: 8 }}>
          Pending canonical locations to review before activation.
        </p>
        {pendingError ? (
          <div className="activities-import-error" role="alert">
            Could not load pending locations ({pendingError}).
          </div>
        ) : null}
        {pendingStatus === "ok" && pendingLocations.length === 0 ? (
          <p className="activities-import-intro" style={{ marginBottom: 0 }}>
            No pending locations.
          </p>
        ) : null}
        {pendingLocations.length > 0 ? (
          <div className="activities-import-review-list">
            {pendingLocations.map((item) => (
              <article key={item.id} className="activities-import-review-item">
                <h3>{item.canonicalName}</h3>
                <p>
                  Scope: <strong>{item.sourceScope}</strong> • Aliases:{" "}
                  <strong>{item.aliasCount}</strong>
                </p>
                <p>
                  Locality: {item.locality || "—"} • Region: {item.region || "—"} •
                  Updated: {item.updatedAt || "—"}
                </p>
                <p>
                  Validation: <strong>{item.validationStatus || "unverified"}</strong>
                  {item.validationProvider ? ` • Provider: ${item.validationProvider}` : ""}
                  {item.validationScore != null
                    ? ` • Score: ${Number(item.validationScore).toFixed(2)}`
                    : ""}
                </p>
                {Array.isArray(item.providerRefs) && item.providerRefs.length > 0 ? (
                  <p>
                    Provider refs:{" "}
                    {item.providerRefs
                      .slice(0, 2)
                      .map((ref) => `${ref.provider}:${ref.placeId}`)
                      .join(" • ")}
                  </p>
                ) : null}
                <div className="activities-import-actions" style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={() => handleReviewAction(item, "approve")}
                    disabled={reviewActionBusyId === item.id}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => handleReviewAction(item, "merge")}
                    disabled={reviewActionBusyId === item.id}
                  >
                    Merge
                  </button>
                  <button
                    type="button"
                    onClick={() => handleReviewAction(item, "reject")}
                    disabled={reviewActionBusyId === item.id}
                  >
                    Reject
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : null}
      </section>

      {history.length > 0 ? (
        <details className="activities-import-history">
          <summary>History ({history.length})</summary>
          {history.map((h) => (
            <div key={`${h.at}-${h.ok}`} className="activities-import-history-item">
              <strong>{h.at}</strong> – {h.dryRun ? "Dry run" : "Write"}{" "}
              – {h.ok ? "OK" : "Error"}
              {h.counters ? (
                <span>
                  {" "}
                  (parsed {h.counters.parsed}, inserted {h.counters.inserted},
                  updated {h.counters.updated})
                </span>
              ) : null}
              {h.error ? <div>{h.error}</div> : null}
            </div>
          ))}
        </details>
      ) : null}
    </div>
  );
}

